import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { deployments } from "@/api/endpoints";
import { DeploymentPicker } from "@/components/shared/DeploymentPicker";
import { Spinner } from "@/components/shared/Spinner";
import { useToast } from "@/components/shared/Toast";
import type { AssignmentResult } from "@/api/endpoints";
import type { DeviceRow } from "@/types/api";

interface Props {
  devices: DeviceRow[];
  onClose: () => void;
}

export function DeploymentAssignDialog({ devices, onClose }: Props) {
  const showToast = useToast((s) => s.show);
  const qc = useQueryClient();
  const [pickedId, setPickedId] = useState<string>("");
  const [result, setResult] = useState<AssignmentResult | null>(null);

  const list = useQuery({
    queryKey: ["deployments", "list"],
    queryFn: () => deployments.list(),
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: () =>
      deployments.assign(
        pickedId,
        devices.map((d) => d.netbox_id),
      ),
    onSuccess: (res) => {
      setResult(res);
      qc.invalidateQueries({ queryKey: ["inventory"] });
      qc.invalidateQueries({ queryKey: ["deployments"] });
      if (res.failed.length === 0) {
        showToast(`Assigned ${res.succeeded.length} device(s) to deployment.`, "success");
        onClose();
      } else {
        showToast(
          `${res.succeeded.length} assigned; ${res.failed.length} failed. See details.`,
          "error",
        );
      }
    },
    onError: (err) => showToast(err instanceof Error ? err.message : "assignment failed", "error"),
  });

  const apply = () => {
    if (!pickedId) return;
    mutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-40 grid place-items-center bg-black/40 px-4">
      <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-xl dark:bg-slate-900">
        <h2 className="text-base font-semibold mb-1">
          Assign {devices.length} device{devices.length === 1 ? "" : "s"} to a Tempo deployment
        </h2>
        <p className="text-xs text-slate-500 mb-4">
          Writes <code className="text-[11px]">tempo_deployment_xref</code> on each device in
          Netbox. To clear an assignment, use the device detail view.
        </p>

        {list.isLoading ? (
          <Spinner label="Loading deployments…" />
        ) : list.error ? (
          <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800 dark:bg-red-950/40 dark:border-red-900">
            <div className="font-medium mb-1">Tempo unavailable</div>
            <div className="text-xs">
              {list.error instanceof Error ? list.error.message : "unknown error"}
            </div>
            <div className="text-xs mt-2 text-red-700">
              Set <code>TEMPO_URL</code>, <code>TEMPO_USERNAME</code>, and{" "}
              <code>TEMPO_PASSWORD</code> in <code>.env</code> and restart the backend.
            </div>
          </div>
        ) : (
          <>
            <div className="block text-sm">
              <span className="text-slate-600 dark:text-slate-300">Deployment</span>
              <div className="mt-1">
                <DeploymentPicker
                  deployments={list.data ?? []}
                  value={pickedId}
                  onChange={setPickedId}
                />
              </div>
            </div>
            {(list.data ?? []).length === 0 && (
              <div className="mt-3 text-sm text-slate-500">
                No deployments listed in Tempo yet.
              </div>
            )}
          </>
        )}

        {result && result.failed.length > 0 && (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3 dark:bg-red-950/40 dark:border-red-900">
            <div className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
              {result.failed.length} failed
            </div>
            <ul className="text-xs text-red-800 dark:text-red-300 space-y-0.5 max-h-40 overflow-auto">
              {result.failed.map((f) => (
                <li key={f.device_id}>device {f.device_id}: {f.error}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-3 py-2 text-sm rounded-md hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            Cancel
          </button>
          <button
            onClick={apply}
            disabled={mutation.isPending || !pickedId || !!list.error}
            className="px-3 py-2 text-sm rounded-md bg-brand-500 text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {mutation.isPending ? "Saving…" : "Assign"}
          </button>
        </div>
      </div>
    </div>
  );
}
